// Entry point for NovaPlataforma frontend
import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import TopBar from './components/TopBar/TopBar';
import SimpleMapView from './components/map/SimpleMapView';
import ReportPage from './components/Report/ReportPage';
import ReportConfigAdmin from './components/Admin/ReportConfigAdmin';
import ReportLayoutAdmin from './components/Admin/ReportLayoutAdmin';
import LoginForm from './components/LoginForm';
import RegisterPage from './components/Register/RegisterPage';
import ProfilePage from './components/Profile/ProfilePage';
import ForgotPasswordPage from './components/PasswordReset/ForgotPasswordPage';
import ResetPasswordPage from './components/PasswordReset/ResetPasswordPage';
import PasswordResetSuccessPage from './components/PasswordReset/PasswordResetSuccessPage';
import ActivateAccountPage from './components/Activation/ActivateAccountPage';
import { MapProvider } from './contexts/MapContext';
import { AuthProvider } from './contexts/AuthContext';
import { PropertyFilterProvider } from './contexts/PropertyFilterContext';
import { GeometryHighlightProvider } from './contexts/GeometryHighlightContext';
import AuthGuard from './components/AuthGuard';
import './index.css';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    background: { default: '#f7f9fa' },
  },
});

/**
 * Layout protegido: só monta MapProvider após autenticação
 * Evita chamadas a APIs que exigem token (ex: municípios) na tela de login
 */
function ProtectedLayout() {
  return (
    <AuthGuard>
      <MapProvider>
        <TopBar />
        <Outlet />
      </MapProvider>
    </AuthGuard>
  );
}

/**
 * Componente principal da aplicação
 * Configura o roteamento, autenticação e tema da aplicação
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password-success" element={<PasswordResetSuccessPage />} />
          <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/" element={<Navigate to="/map" replace />} />

          {/* Rotas protegidas — MapProvider só monta após AuthGuard */}
          <Route element={<ProtectedLayout />}>
            <Route
              path="/map"
              element={
                <PropertyFilterProvider>
                  <GeometryHighlightProvider>
                    <SimpleMapView />
                  </GeometryHighlightProvider>
                </PropertyFilterProvider>
              }
            />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/admin/report-config" element={<ReportConfigAdmin />} />
            <Route path="/admin/report-layout" element={<ReportLayoutAdmin />} />
            <Route
              path="/profile"
              element={
                <PropertyFilterProvider>
                  <GeometryHighlightProvider>
                    <ProfilePage />
                  </GeometryHighlightProvider>
                </PropertyFilterProvider>
              }
            />
          </Route>

          {/* Redirecionar todas as outras rotas para o mapa */}
          <Route path="*" element={<Navigate to="/map" replace />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
